import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { Task, TaskFormData } from '../../services/tasks';
import { Planner } from '../../services/planners';
import { Topic } from '../../services/topics';

interface TaskFormProps {
  open: boolean;
  task: Task | null;
  planners: Planner[];
  topics?: Topic[];
  defaultTopicId?: string | null;
  onSave: (data: TaskFormData) => void;
  onClose: () => void;
  onDelete?: (task: Task) => void;
}

const initialForm: TaskFormData = {
  title: '',
  description: '',
  status: 'pending',
  priority: 'medium',
  due_date: '',
  assigned_planner_id: '',
  topic_id: '',
  scheduling_constraints: '',
  notes: '',
  budget_expected: null,
  budget_actual: null,
  budget_paid: null,
  payment_on_wedding_day: false,
};

const hasBudgetValues = (t: Task) =>
  (t.budget_expected != null && t.budget_expected !== 0) ||
  (t.budget_actual != null && t.budget_actual !== 0) ||
  (t.budget_paid != null && t.budget_paid !== 0);

const TaskForm: React.FC<TaskFormProps> = ({ open, task, planners, topics = [], defaultTopicId, onSave, onClose, onDelete }) => {
  const [form, setForm] = useState<TaskFormData>(initialForm);
  const [hasBudget, setHasBudget] = useState(false);

  useEffect(() => {
    if (task) {
      const budgetActive = hasBudgetValues(task);
      setHasBudget(budgetActive);
      setForm({
        title: task.title,
        description: task.description || '',
        status: task.status,
        priority: task.priority,
        due_date: task.due_date ? task.due_date.slice(0, 10) : '',
        assigned_planner_id: task.assigned_planner_id || '',
        topic_id: task.topic_id || '',
        scheduling_constraints: task.scheduling_constraints || '',
        notes: task.notes || '',
        budget_expected: task.budget_expected ?? null,
        budget_actual: task.budget_actual ?? null,
        budget_paid: task.budget_paid ?? null,
        payment_on_wedding_day: task.payment_on_wedding_day ?? false,
      });
    } else {
      setHasBudget(false);
      setForm({ ...initialForm, topic_id: defaultTopicId || '' });
    }
  }, [task, open, defaultTopicId]);

  const handleChange = (field: string, value: any) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleBudgetNumberChange = (field: string, value: string) => {
    const parsed = value === '' ? null : parseFloat(value);
    setForm((prev) => ({ ...prev, [field]: parsed == null || isNaN(parsed) ? null : parsed }));
  };

  const handleSubmit = () => {
    const data: TaskFormData = {
      ...form,
      assigned_planner_id: form.assigned_planner_id || null,
      topic_id: form.topic_id || null,
      due_date: form.due_date || null,
      budget_expected: hasBudget ? (form.budget_expected ?? null) : null,
      budget_actual: hasBudget ? (form.budget_actual ?? null) : null,
      budget_paid: hasBudget ? (form.budget_paid ?? null) : null,
      payment_on_wedding_day: hasBudget ? (form.payment_on_wedding_day ?? false) : false,
    };
    onSave(data);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{task ? 'Edit Task' : 'New Task'}</DialogTitle>
      <DialogContent
        sx={{
          '& .MuiInputLabel-shrink': {
            bgcolor: 'background.paper',
            px: 0.5,
          },
        }}
      >
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid size={12}>
            <TextField
              label="Title"
              fullWidth
              required
              value={form.title}
              onChange={(e) => handleChange('title', e.target.value)}
            />
          </Grid>
          <Grid size={12}>
            <TextField
              label="Description"
              fullWidth
              multiline
              rows={2}
              value={form.description}
              onChange={(e) => handleChange('description', e.target.value)}
            />
          </Grid>
          <Grid size={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={form.status}
                label="Status"
                onChange={(e) => handleChange('status', e.target.value)}
              >
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="in_progress">In Progress</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Priority</InputLabel>
              <Select
                value={form.priority}
                label="Priority"
                onChange={(e) => handleChange('priority', e.target.value)}
              >
                <MenuItem value="low">Low</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="high">High</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={6}>
            <TextField
              label="Due Date"
              type="date"
              fullWidth
              value={form.due_date}
              onChange={(e) => handleChange('due_date', e.target.value)}
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </Grid>
          <Grid size={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Assigned Planner</InputLabel>
              <Select
                value={form.assigned_planner_id}
                label="Assigned Planner"
                onChange={(e) => handleChange('assigned_planner_id', e.target.value)}
              >
                <MenuItem value="">None</MenuItem>
                {planners.map((p) => (
                  <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          {topics.length > 0 && (
            <Grid size={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Topic</InputLabel>
                <Select
                  value={form.topic_id}
                  label="Topic"
                  onChange={(e) => handleChange('topic_id', e.target.value)}
                >
                  <MenuItem value="">None (standalone)</MenuItem>
                  {topics.map((t) => (
                    <MenuItem key={t.id} value={t.id}>
                      {t.emoji && `${t.emoji} `}{t.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
          <Grid size={12}>
            <TextField
              label="Scheduling Constraints"
              fullWidth
              multiline
              rows={2}
              value={form.scheduling_constraints}
              onChange={(e) => handleChange('scheduling_constraints', e.target.value)}
            />
          </Grid>
          <Grid size={12}>
            <TextField
              label="Notes"
              fullWidth
              multiline
              rows={2}
              value={form.notes}
              onChange={(e) => handleChange('notes', e.target.value)}
            />
          </Grid>
          <Grid size={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={hasBudget}
                  onChange={(e) => setHasBudget(e.target.checked)}
                />
              }
              label={<Typography variant="body2" fontWeight={500}>Has Budget</Typography>}
            />
          </Grid>
          {hasBudget && (
            <>
              <Grid size={4}>
                <TextField
                  label="Expected Cost"
                  type="number"
                  fullWidth
                  value={form.budget_expected ?? ''}
                  onChange={(e) => handleBudgetNumberChange('budget_expected', e.target.value)}
                  slotProps={{ htmlInput: { min: 0, step: 0.01 } }}
                />
              </Grid>
              <Grid size={4}>
                <TextField
                  label="Actual Cost"
                  type="number"
                  fullWidth
                  value={form.budget_actual ?? ''}
                  onChange={(e) => handleBudgetNumberChange('budget_actual', e.target.value)}
                  slotProps={{ htmlInput: { min: 0, step: 0.01 } }}
                />
              </Grid>
              <Grid size={4}>
                <TextField
                  label="Paid"
                  type="number"
                  fullWidth
                  value={form.budget_paid ?? ''}
                  onChange={(e) => handleBudgetNumberChange('budget_paid', e.target.value)}
                  slotProps={{ htmlInput: { min: 0, step: 0.01 } }}
                />
              </Grid>
              <Grid size={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.payment_on_wedding_day ?? false}
                      onChange={(e) => handleChange('payment_on_wedding_day', e.target.checked)}
                    />
                  }
                  label={<Typography variant="body2">Payment on Wedding Day</Typography>}
                />
              </Grid>
            </>
          )}
        </Grid>
      </DialogContent>
      <DialogActions sx={{ justifyContent: task && onDelete ? 'space-between' : 'flex-end' }}>
        {task && onDelete && (
          <Button onClick={() => onDelete(task)} sx={{ color: 'error.main' }}>Delete</Button>
        )}
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={onClose} sx={{ color: 'grey.600' }}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" disabled={!form.title.trim()}>
            {task ? 'Save' : 'Create'}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default TaskForm;
